/**
 * Central configuration file for all application constants
 * This file centralizes all hardcoded values to improve maintainability
 */

// Department configuration
export const DEPARTMENTS = [
  'Tecnologia',
  'Atendimento',
  'BI',
  'Comercial',
  'Contas',
  'Criação',
  'Diretoria',
  'Financeiro',
  'Planejamento',
  'Produção',
  'Produção Financeira',
  'SEO',
  'UX'
] as const;

export type Department = typeof DEPARTMENTS[number];

// Contract Type configuration
export enum ContractType {
  CLT = 'CLT',
  PJ = 'PJ',
  SOCIO = 'SOCIO'
}

// Performance Level configuration
export enum PerformanceLevel {
  HIGH_PERFORMANCE = 'Alta Performance',
  GROWTH_POTENTIAL = 'Potencial Crescimento',
  DEVELOPING = 'Desenvolvimento',
  MEETING_EXPECTATIONS = 'Cumpre Função',
  UNDER_REVIEW = 'Sob Análise',
  MISALIGNED = 'Desalinhado'
}

// UI Labels configuration
export const UI_LABELS = {
  ALL_FILTER: 'Todos',
  NO_SELECTION: 'Selecione...',
  LOADING: 'Carregando...',
  ERROR: 'Erro ao carregar dados',
  RETRY: 'Tentar novamente',
  NO_DATA: 'Sem dados disponíveis'
} as const;

// API Response Status configuration
export enum ApiStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

// Employee Status configuration
export enum EmployeeStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  ONBOARDING = 'onboarding',
  TERMINATED = 'terminated'
}

// Meeting Status configuration
export enum MeetingStatus {
  SCHEDULED = 'scheduled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

// Training Status configuration
export enum TrainingStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  FAILED = 'failed',
  EXPIRED = 'expired'
}

// Vacation Status configuration
export enum VacationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed'
}

// Permission Levels configuration
export enum PermissionLevel {
  ADMIN = 'admin',
  MANAGER = 'manager',
  EMPLOYEE = 'employee',
  VIEWER = 'viewer'
}

// Export all constants as a single object for easier debugging
export const APP_CONSTANTS = {
  DEPARTMENTS,
  CONTRACT_TYPES: Object.values(ContractType),
  PERFORMANCE_LEVELS: Object.values(PerformanceLevel),
  EMPLOYEE_STATUSES: Object.values(EmployeeStatus),
  MEETING_STATUSES: Object.values(MeetingStatus),
  TRAINING_STATUSES: Object.values(TrainingStatus),
  VACATION_STATUSES: Object.values(VacationStatus),
  PERMISSION_LEVELS: Object.values(PermissionLevel),
  UI_LABELS
} as const;

// Validation helpers
export const isValidDepartment = (value: string): value is Department => {
  return DEPARTMENTS.includes(value as Department);
};

export const isValidContractType = (value: string): value is ContractType => {
  return Object.values(ContractType).includes(value as ContractType);
};

export const isValidPerformanceLevel = (value: string): value is PerformanceLevel => {
  return Object.values(PerformanceLevel).includes(value as PerformanceLevel);
};